import { createStore, createEffect, sample, combine } from "effector";
import { $student } from "@features/student/auth/model";
import { $identificationCompleted, flagsUpdated } from "@features/flagsmith/model";
import { $personalPlanEnabled } from "@features/personal-plan/model";
import {
    setHasRepliesDataSafe,
    setPersonalPlanGroupSafe,
    setPersonalPlanOnProductOnboardingSafe
} from "@features/shared/events";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IOnboardingCurrentScreenContract } from "@features/onboarding/ui/current";
import { IProductOnboardingContract } from "@features/onboarding/ui/productOnboarding";
import { ICoursesContract } from "@features/courses/model";

const sendHasRepliesDataFx = createEffect((hasReplies: boolean) => {
    setHasRepliesDataSafe(hasReplies);
});

const sendPersonalPlanGroupFx = createEffect((enabled: boolean) => {
    setPersonalPlanGroupSafe(enabled);
});

const sendPersonalPlanOnProductOnboardingFx = createEffect(() => {
    setPersonalPlanOnProductOnboardingSafe(true);
});

const contract = new ModelContract({}, {}, {}, {});

export type IProductOnboardingEventsContract = typeof contract;

export class ProductOnboardingEventsModule extends ModuleDescription<IProductOnboardingEventsContract> {
    constructor(
        private currentScreenContract: IOnboardingCurrentScreenContract,
        private productOnboardingContract: IProductOnboardingContract,
        private coursesContract: ICoursesContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        const $hasRepliesDataSent = createStore(false);
        const $personalPlanGroupSent = createStore(false);
        const $personalPlanOnProductOnboardingSent = createStore(false);

        const $challenges = combine(this.coursesContract.stores.$courses, (courses) =>
            courses.filter((course) => course.type === "challenge")
        );

        sample({
            clock: [$student, $identificationCompleted],
            source: {
                student: $student,
                identificationCompleted: $identificationCompleted,
                hasRepliesDataSent: $hasRepliesDataSent
            },
            filter: ({ student, identificationCompleted, hasRepliesDataSent }) =>
                !hasRepliesDataSent && identificationCompleted && student !== null,
            fn: ({ student }) => !!student?.sessionId || !!student?.hasSavedOnboardingData,
            target: sendHasRepliesDataFx
        });

        sample({
            clock: [$personalPlanEnabled, $identificationCompleted, flagsUpdated],
            source: {
                personalPlanEnabled: $personalPlanEnabled,
                identificationCompleted: $identificationCompleted,
                personalPlanGroupSent: $personalPlanGroupSent
            },
            filter: ({ identificationCompleted, personalPlanGroupSent }) =>
                !personalPlanGroupSent && identificationCompleted,
            fn: ({ personalPlanEnabled }) => personalPlanEnabled,
            target: sendPersonalPlanGroupFx
        });

        sample({
            clock: flagsUpdated,
            fn: () => false,
            target: [$personalPlanGroupSent, $personalPlanOnProductOnboardingSent, $hasRepliesDataSent]
        });

        sample({
            clock: [
                this.currentScreenContract.stores.$step,
                this.productOnboardingContract.stores.$isProductOnboardingModalOpen,
                $personalPlanEnabled,
                $challenges
            ],
            source: {
                step: this.currentScreenContract.stores.$step,
                modalOpen: this.productOnboardingContract.stores.$isProductOnboardingModalOpen,
                personalPlanEnabled: $personalPlanEnabled,
                challenges: $challenges,
                personalPlanOnProductOnboardingSent: $personalPlanOnProductOnboardingSent
            },
            filter: ({ step, modalOpen, personalPlanEnabled, challenges, personalPlanOnProductOnboardingSent }) =>
                !personalPlanOnProductOnboardingSent &&
                step === 6 &&
                modalOpen &&
                personalPlanEnabled &&
                challenges.length > 0,
            target: sendPersonalPlanOnProductOnboardingFx
        });

        sample({
            clock: sendHasRepliesDataFx.done,
            fn: () => true,
            target: $hasRepliesDataSent
        });

        sample({
            clock: sendPersonalPlanGroupFx.done,
            fn: () => true,
            target: $personalPlanGroupSent
        });

        sample({
            clock: sendPersonalPlanOnProductOnboardingFx.done,
            fn: () => true,
            target: $personalPlanOnProductOnboardingSent
        });
    }
}
